import * as React from 'react'
import PropTypes from 'prop-types'
import { dataProps, useMediaCondition } from '@ds/react-utils'
import type { ButtonProps } from '../../../components/Button'
import { isPhoneInLandscape } from '../../../styles'
import { useIsInk, useThemeStyles } from '../../../theming'
import styles from './styles'

export interface PopoverFooterProps {
  /**
   * Optional content to display alongside the footer buttons.
   */
  content?: React.ReactNode
  /**
   * Set to true to use PopoverFooter on dark backgrounds.
   */
  dark?: boolean
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /**
   * The primary action - accepts an @ds/ui Button component.
   */
  primaryButton?: React.ReactElement<ButtonProps>
  /**
   * The secondary action - accepts an @ds/ui Button component.
   */
  secondaryButton?: React.ReactElement<ButtonProps>
}

export function PopoverFooter({
  content,
  dark = false,
  primaryButton,
  secondaryButton,
  ...restProps
}: PopoverFooterProps) {
  const sx = useThemeStyles(styles, { dark })
  const isInk = useIsInk()

  const phoneInLandscape = useMediaCondition(
    isPhoneInLandscape.replace('@media', ''),
  )

  const inkFooter = isInk && (
    <div css={sx.default.wrap}>
      {content && (
        <div css={sx.default.content} {...dataProps(restProps)}>
          {content}
        </div>
      )}
      <div css={sx.default.buttonsWrapper}>
        {phoneInLandscape ? (
          <>
            <div css={sx.default.primary}>{primaryButton}</div>
            <div css={sx.default.secondary}>{secondaryButton}</div>
          </>
        ) : (
          <>
            <div css={sx.default.secondary}>{secondaryButton}</div>
            <div css={sx.default.primary}>{primaryButton}</div>
          </>
        )}
      </div>
    </div>
  )

  const oliveFooter = !isInk && (
    <div css={sx.default.wrap}>
      <div css={sx.default.buttonsWrapper}>
        <div css={sx.default.primary}>{primaryButton}</div>
        <div css={sx.default.secondary}>{secondaryButton}</div>
      </div>

      {content && (
        <div css={sx.default.content} {...dataProps(restProps)}>
          {content}
        </div>
      )}
    </div>
  )

  return oliveFooter || inkFooter || null
}

PopoverFooter.propTypes = {
  content: PropTypes.node,
  dark: PropTypes.bool,
  'data-.*': PropTypes.string,
  primaryButton: PropTypes.element,
  secondaryButton: PropTypes.element,
}
